/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: PhonebookEntriesView.tsx
 🗓️ Created: 2025-12-05
 🧩 Description:
    View phonebook entries (internal from modules or external contacts)
────────────────────────────────────────────────────────────────*/

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BookOpen, User, Phone, Users, Video, Hash, Zap } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { ModuleFormHeader } from '../../components/layouts/ModuleFormHeader';
import { FormBox } from '../../components/ui/FormBox';
import { fetchPhonebookById, fetchPhonebookEntries } from './api/phonebooksApi';
import type { Phonebook, PhonebookEntry, InternalPhonebookEntry, PhonebookContact } from './types';

export default function PhonebookEntriesView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { showError } = useNotifications();

  const [loading, setLoading] = useState(false);
  const [phonebook, setPhonebook] = useState<Phonebook | null>(null);
  const [entries, setEntries] = useState<PhonebookEntry[]>([]);

  useEffect(() => {
    if (id) {
      loadData();
    }
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [phonebookData, entriesData] = await Promise.all([
        fetchPhonebookById(id!),
        fetchPhonebookEntries(id!),
      ]);
      setPhonebook(phonebookData);
      setEntries(entriesData);
    } catch (error) {
      showError(error instanceof Error ? error.message : t('phonebooks.messages.loadEntriesError'));
    } finally {
      setLoading(false);
    }
  };

  const getSourceIcon = (source: string) => {
    switch (source) {
      case 'extension':
        return <User className="h-4 w-4 text-blue-600" />;
      case 'queue':
        return <Users className="h-4 w-4 text-purple-600" />;
      case 'ringgroup':
        return <Phone className="h-4 w-4 text-green-600" />;
      case 'conference':
        return <Video className="h-4 w-4 text-orange-600" />;
      case 'featurecode':
        return <Hash className="h-4 w-4 text-pink-600" />;
      case 'speeddial':
        return <Zap className="h-4 w-4 text-yellow-600" />;
      default:
        return <Phone className="h-4 w-4 text-gray-600" />;
    }
  };

  const isInternalEntry = (entry: PhonebookEntry): entry is InternalPhonebookEntry => {
    return 'source' in entry;
  };

  const isExternalContact = (entry: PhonebookEntry): entry is PhonebookContact => {
    return 'phonebookId' in entry;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <ModuleFormHeader
        icon={<BookOpen className="h-5 w-5" />}
        title={phonebook?.description || t('phonebooks.viewEntries')}
        backButtonText={t('phonebooks.actions.backToList')}
        onBack={() => navigate('/pbx/applications/phonebooks')}
      />

      <div className="mt-8">
        <FormBox
          title={`${t('phonebooks.entries')} (${entries.length})`}
          actions={phonebook?.type === 'external' ? [
            {
              label: t('phonebooks.actions.importCSV'),
              onClick: () => navigate(`/pbx/applications/phonebooks/${id}/import`),
            }
          ] : []}
        >
          {entries.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              {t('phonebooks.noEntriesFound')}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                      {t('phonebooks.columns.name')}
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                      {t('phonebooks.columns.number')}
                    </th>
                    {phonebook?.type === 'internal' && (
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                        {t('phonebooks.columns.source')}
                      </th>
                    )}
                    {phonebook?.type === 'external' && (
                      <>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                          {t('phonebooks.columns.mobile')}
                        </th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                          {t('phonebooks.columns.organization')}
                        </th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry, index) => (
                    <tr key={index} className="border-b border-border hover:bg-accent/50 transition-colors">
                      <td className="py-3 px-4">
                        {isInternalEntry(entry) ? (
                          <div className="flex items-center gap-2">
                            {getSourceIcon(entry.source)}
                            <span className="font-medium">{entry.name}</span>
                          </div>
                        ) : isExternalContact(entry) ? (
                          <span className="font-medium">
                            {entry.firstName} {entry.lastName || ''}
                          </span>
                        ) : null}
                      </td>
                      <td className="py-3 px-4 font-mono text-sm">
                        {isInternalEntry(entry) ? entry.number : isExternalContact(entry) ? entry.phoneNumber : '-'}
                      </td>
                      {phonebook?.type === 'internal' && isInternalEntry(entry) && (
                        <td className="py-3 px-4">
                          <span className="text-xs text-muted-foreground capitalize">
                            {entry.source}
                          </span>
                        </td>
                      )}
                      {phonebook?.type === 'external' && isExternalContact(entry) && (
                        <>
                          <td className="py-3 px-4 font-mono text-sm text-muted-foreground">
                            {entry.mobileNumber || '-'}
                          </td>
                          <td className="py-3 px-4 text-sm text-muted-foreground">
                            {entry.organization || '-'}
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </FormBox>
      </div>
    </div>
  );
}

